import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import { cn } from '@/lib/cn';

import IconButton from '../Button/IconButton';
import Combobox from '../Combobox';
import type {
  ComboboxOption,
  ComboboxOptionComponent,
  ComboboxOptionRenderer,
} from '../Combobox/types';
import Icon from '../Icon/Icon';
import { Button } from '../ui/button';

type Props<TOption extends ComboboxOption> = {
  id: string;
  value?: string;
  label?: string;
  onChange: (option?: TOption) => void;
  fetchOptions?: (text?: string) => Promise<TOption[]>;
  renderOption?:
    | ComboboxOptionRenderer<TOption>
    | ComboboxOptionComponent<TOption>;
  disabled?: boolean;
  hideSearch?: boolean;
  allowEmpty?: boolean;
  width?: number | string;
  error?: boolean;
  onScrollToEnd?: () => void;
  className?: string;
};

/**
 * Combobox を含む、単体で活用可能なセレクト
 */
export function ControlledSelect<TOption extends ComboboxOption>({
  className,
  ...props
}: Props<TOption>) {
  const tc = useCommonTranslation();

  const [open, setOpen] = useState(false);
  const popoverContentId = useId();
  const [searchText, setSearchText] = useState('');
  const debouncedSetSearchText = useDebouncedCallback(
    (text: string) => setSearchText(text),
    [],
  );

  const valueToLabelCache = useValueToLabelCache();

  const { data: options, isPending } = useQuery({
    queryKey: props.fetchOptions ? ['fetchOptions', props.id, searchText] : [],
    queryFn: () => props.fetchOptions?.(searchText) ?? [],
    initialData: [],
  });

  // labelの計算：キャッシュを優先し、optionsから検索、最後にvalueをfallback
  const label = useMemo(() => {
    if (!props.value) return undefined;

    // 1. まずキャッシュから取得を試行
    const cachedLabel = valueToLabelCache.get(props.value);
    if (cachedLabel) return cachedLabel;

    // 2. optionsから検索
    const optionLabel = options?.find(
      (option) => option.value === props.value,
    )?.label;
    if (optionLabel) return optionLabel;

    // 3. fallbackとしてlabel or valueを使用
    return props.label ?? props.value;
  }, [options, props.value, props.label, valueToLabelCache]);

  const canClear = Boolean(props.allowEmpty && props.value && !props.disabled);

  useEffect(() => {
    if (!open) {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
      setSearchText('');
    }
  }, [open]);

  /** CForm の `Input` と同じ既定幅（px）。文字列指定時はクラスで幅を制御 */
  const numericTriggerWidth =
    typeof props.width === 'number'
      ? props.width
      : props.width === undefined
        ? 180
        : null;
  const widthClassName =
    typeof props.width === 'string' ? props.width : undefined;

  return (
    <Popover open={open} onOpenChange={() => setOpen(!open)}>
      <div
        className={cn('relative inline-block min-w-0', widthClassName)}
        style={
          numericTriggerWidth != null
            ? { width: numericTriggerWidth }
            : undefined
        }
      >
        <PopoverTrigger asChild>
          <Button
            id={props.id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-controls={popoverContentId}
            aria-invalid={props.error ? true : undefined}
            className={cn(
              'relative w-full justify-start overflow-hidden text-ellipsis pr-10',
              className,
              !label && 'text-muted-foreground',
              props.error &&
                'border-destructive focus-visible:ring-2 focus-visible:ring-destructive',
            )}
            disabled={props.disabled}
          >
            <span className={cn('min-w-0 truncate', canClear && 'pr-8')}>
              {label || tc('pleaseSelect')}
            </span>
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <Icon
                iconName="ChevronDownIcon"
                className="shrink-0 opacity-50"
              />
            </div>
          </Button>
        </PopoverTrigger>

        {canClear && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton
              icon="XIcon"
              variant="mutedGhost"
              size="ExtraSmall"
              aria-label="Clear selection"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onChange(undefined);
                setOpen(false);
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        )}
      </div>
      <PopoverContent
        id={popoverContentId}
        className={cn('max-h-96 w-full overflow-hidden p-0', widthClassName)}
        style={
          numericTriggerWidth != null
            ? { width: numericTriggerWidth }
            : undefined
        }
        align="start"
      >
        <Combobox
          type="single"
          value={props.value ?? null}
          selectionDisplayStyle="hidden"
          onValueChange={(value) => {
            const option = options?.find((option) => option.value === value);
            if (!props.allowEmpty && option == null) {
              return;
            }

            // 選択時にキャッシュを自動更新
            if (option?.value && option?.label) {
              valueToLabelCache.set(option.value, option.label);
            }

            props.onChange(option);
            setOpen(false);
          }}
          renderOption={props.renderOption}
          isPending={isPending}
          isFetching={isPending}
          options={options ?? []}
          onScrollToEnd={props.onScrollToEnd}
          onSearchChange={debouncedSetSearchText}
          valueToLabelCache={valueToLabelCache}
          hideSearch={props.hideSearch}
        />
      </PopoverContent>
    </Popover>
  );
}
